import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { DialogService } from '../../../../shared/services/dialog.service';
import { ValidationRulesFacade } from '../../facade/validation-rules.facade';
import { IRuleTypeDefinition } from '../../models/validation-rule';
import { RuleTypeDefinitionModalComponent } from '../../modals/rule-type-definition-modal/rule-type-definition-modal.component';

/**
 * Rule Types admin tab.
 *
 * Lists every ``RuleTypeDefinition`` so admins can edit labels,
 * descriptions, sort order, is_active, and manage the per-rule-type
 * ``RuleParamDefinition`` rows. The underlying ``RuleOperation`` rows
 * are read-only (backed by Python code), surfaced here as a sidebar of
 * available ops the admin can choose from when creating a new rule type
 * definition.
 *
 * Grouped by ``kind`` (Field vs Cross-field) with collapse handles so
 * the long list doesn't dominate the page.
 */
@Component({
  selector: 'app-rule-types-tab',
  standalone: true,
  imports: [CommonModule, FontAwesomeModule, RuleTypeDefinitionModalComponent],
  templateUrl: './rule-types-tab.component.html',
})
export class RuleTypesTabComponent implements OnInit {
  private facade = inject(ValidationRulesFacade);
  private dialog = inject(DialogService);

  ruleTypeDefs = this.facade.ruleTypeDefsSignal;
  operations = this.facade.operationsSignal;

  // Grouped by kind for the accordion display.
  fieldDefs = computed(() =>
    this.ruleTypeDefs()
      .filter((d) => d.kind === 'field')
      .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)),
  );
  crossDefs = computed(() =>
    this.ruleTypeDefs()
      .filter((d) => d.kind === 'cross')
      .sort((a, b) => a.sort_order - b.sort_order || a.code.localeCompare(b.code)),
  );

  modalId = 'rule-type-definition-modal';
  editingDef = signal<IRuleTypeDefinition | null>(null);

  ngOnInit(): void {
    this.facade.loadOperations();
    this.facade.loadRuleTypeDefinitions();
  }

  beginEdit(def: IRuleTypeDefinition): void {
    this.editingDef.set(def);
    this.dialog.open(this.modalId);
  }

  beginCreate(): void {
    // Open the modal with a null def; the modal renders an empty form.
    this.editingDef.set(null);
    this.dialog.open(this.modalId);
  }

  onModalClosed(): void {
    this.editingDef.set(null);
    this.dialog.close(this.modalId);
  }

  /**
   * Promote the modal from CREATE mode to EDIT mode for the newly-saved
   * rule type definition. The modal stays open so the admin can add
   * params immediately — the params editor only renders once there's a
   * concrete `definition` to attach `RuleParamDefinition`s to.
   */
  onModalCreated(def: IRuleTypeDefinition): void {
    this.editingDef.set(def);
  }

  toggleActive(def: IRuleTypeDefinition): void {
    this.facade.patchRuleTypeDefinition(def.id, { is_active: !def.is_active });
  }

  trackById(_: number, d: IRuleTypeDefinition): number {
    return d.id;
  }

  paramSummary(def: IRuleTypeDefinition): string {
    if (!def.param_defs || def.param_defs.length === 0) return '(no params)';
    return def.param_defs.map((p) => `${p.key}${p.is_required ? '*' : ''}`).join(', ');
  }

  opLabelFor(def: IRuleTypeDefinition): string {
    const op = this.operations().find((o) => o.id === def.operation);
    return op ? `${op.code}` : `op #${def.operation}`;
  }
}
